import React, { useState, useEffect, useMemo } from 'react';
import { AñoEscolar, Grado, Seccion } from 'types/usuario.types';
import { PeriodoResponse } from 'types/periodo.types';
import { catalogoService } from 'services/usuarioService';
import { periodoService } from 'services/periodoService';
import { documentoService } from 'services/documentoService';
import { exportarPDFMasivo } from 'utils/reporteExport';
import { htmlBoleta, htmlBoletaSIAGIE } from 'utils/documentoPdf';
import { fmtAnioEscolar } from 'utils/anioEscolar';
import { HttpError } from 'utils/httpClient';
import { catalogStorage } from 'utils/catalogStorage';
import { useConfigInstitucional } from 'hooks/useConfigInstitucional';

// ============================================================
// BoletasMasivaPage – Generación masiva de boletas por sección
// Roles: DIRECTOR, SECRETARIA
// ============================================================

const SpinnerIcon = ({ cls = 'w-5 h-5 text-white' }: { cls?: string }) => (
  <svg className={`animate-spin ${cls}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const Select: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string | number; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}> = ({ label, value, onChange, options, placeholder = 'Seleccionar…', disabled }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">{label}</label>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm
                 text-slate-700 outline-none focus:ring-2 focus:ring-blue-500
                 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const BoletasMasivaPage: React.FC = () => {
  const { getValor } = useConfigInstitucional();
  const usaCompetencias = getValor('EVAL_USA_COMPETENCIAS') === 'true';

  const [años, setAños]       = useState<AñoEscolar[]>([]);
  const [grados, setGrados]   = useState<Grado[]>([]);
  const [secciones, setSecciones] = useState<Seccion[]>([]);
  const [periodos, setPeriodos]   = useState<PeriodoResponse[]>([]);

  const [anioId, setAnioId]       = useState('');
  const [nivel, setNivel]         = useState('');
  const [gradoId, setGradoId]     = useState('');
  const [seccionId, setSeccionId] = useState('');
  const [periodoId, setPeriodoId] = useState('');

  const [loadingCatalogos, setLoadingCatalogos] = useState(true);
  const [loadingPeriodos, setLoadingPeriodos]   = useState(false);
  const [generando, setGenerando]               = useState(false);
  const [generandoSiagie, setGenerandoSiagie]   = useState(false);
  const [error, setError]                       = useState<string | null>(null);
  const [resultado, setResultado]               = useState<number | null>(null);

  useEffect(() => {
    const init = async () => {
      setLoadingCatalogos(true);
      try {
        const [aArr, gArr, sArr] = await Promise.all([
          catalogoService.getAñosEscolares(),
          catalogoService.getGrados(),
          catalogoService.getSecciones(),
        ]);
        setAños(aArr);
        setGrados(gArr);
        setSecciones(sArr);
        if (aArr.length > 0) setAnioId(String(aArr[0].idAnioEscolar));
      } finally {
        setLoadingCatalogos(false);
      }
    };
    init();
  }, []);

  // Cargar períodos al cambiar año
  useEffect(() => {
    if (!anioId) { setPeriodos([]); setPeriodoId(''); return; }
    setLoadingPeriodos(true);
    periodoService.listarPorAnioEscolar(Number(anioId))
      .then(ps => { setPeriodos(ps); setPeriodoId(''); })
      .catch(() => setPeriodos([]))
      .finally(() => setLoadingPeriodos(false));
  }, [anioId]);

  // Niveles únicos derivados del catálogo de grados
  const niveles = useMemo(() => {
    const set = new Set(grados.map(g => g.nivel));
    return Array.from(set).sort();
  }, [grados]);

  const gradosFiltrados = useMemo(() =>
    nivel ? grados.filter(g => g.nivel === nivel) : grados,
  [grados, nivel]);

  const seccionesFiltradas = useMemo(() =>
    gradoId ? secciones.filter(s => s.gradoId === Number(gradoId)) : [],
  [secciones, gradoId]);

  const handleNivelChange = (v: string) => {
    setNivel(v);
    setGradoId('');
    setSeccionId('');
    setError(null);
    setResultado(null);
  };

  const handleGradoChange = (v: string) => {
    setGradoId(v);
    setSeccionId('');
    setError(null);
    setResultado(null);
  };

  const canGenerar      = anioId && gradoId && seccionId && periodoId && !generando && !generandoSiagie;
  const canGenerarSiagie = anioId && gradoId && seccionId && !generando && !generandoSiagie && usaCompetencias;

  const handleGenerarSiagie = async () => {
    if (!canGenerarSiagie) return;
    setGenerandoSiagie(true);
    setError(null);
    setResultado(null);
    try {
      const boletas = await documentoService.getBoletasSIAGIEMasivo({
        anioEscolarId: Number(anioId),
        gradoId:       Number(gradoId),
        seccionId:     Number(seccionId),
      });
      if (boletas.length === 0) {
        setError('No se encontraron estudiantes matriculados activos en esta sección.');
        return;
      }
      const grado   = grados.find(g => g.idGrado === Number(gradoId));
      const seccion = secciones.find(s => s.idSeccion === Number(seccionId));
      const año     = años.find(a => a.idAnioEscolar === Number(anioId));
      const sello = catalogStorage.getSelloBase64();
      exportarPDFMasivo(
        `Informe de Progreso SIAGIE — ${grado?.nombre} Sección ${seccion?.nombre} — ${año ? fmtAnioEscolar(año) : ''}`,
        boletas.map(b => htmlBoletaSIAGIE({
          ...b,
          institucion: { ...b.institucion, selloBase64: sello },
        })),
      );
      setResultado(boletas.length);
    } catch (e: unknown) {
      if (e instanceof HttpError && e.status === 404) {
        setError('No hay estudiantes matriculados activos en esta sección.');
      } else {
        setError(e instanceof Error ? e.message : 'Error al generar boletas SIAGIE');
      }
    } finally {
      setGenerandoSiagie(false);
    }
  };

  const handleGenerar = async () => {
    if (!canGenerar) return;
    setGenerando(true);
    setError(null);
    setResultado(null);
    try {
      const boletas = await documentoService.getBoletasMasivo({
        anioEscolarId: Number(anioId),
        gradoId:       Number(gradoId),
        seccionId:     Number(seccionId),
        periodoId:     Number(periodoId),
      });

      if (boletas.length === 0) {
        setError('No se encontraron estudiantes matriculados en esta sección para el período seleccionado.');
        return;
      }

      const grado   = grados.find(g => g.idGrado === Number(gradoId));
      const seccion = secciones.find(s => s.idSeccion === Number(seccionId));
      const periodo = periodos.find(p => p.idPeriodo === Number(periodoId));
      const año     = años.find(a => a.idAnioEscolar === Number(anioId));

      exportarPDFMasivo(
        `Boletas — ${grado?.nombre} Sección ${seccion?.nombre} — ${periodo?.nombre} — ${año ? fmtAnioEscolar(año) : ''}`,
        boletas.map(b => htmlBoleta(b)),
      );
      setResultado(boletas.length);
    } catch (e: unknown) {
      if (e instanceof HttpError && e.status === 404) {
        setError('No hay estudiantes matriculados activos en esta sección para el período seleccionado.');
      } else {
        setError(e instanceof Error ? e.message : 'Error al generar las boletas');
      }
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Boletas de Notas Masivas</h1>
        <p className="text-blue-200 mt-1">
          Genera e imprime las boletas de toda una sección en un solo documento
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h2 className="text-sm font-bold text-slate-700 mb-4">Selecciona la sección y período</h2>

        {loadingCatalogos ? (
          <div className="flex justify-center py-8">
            <SpinnerIcon cls="w-6 h-6 text-blue-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Select
              label="Año Escolar"
              value={anioId}
              onChange={v => { setAnioId(v); setNivel(''); setGradoId(''); setSeccionId(''); setPeriodoId(''); setError(null); setResultado(null); }}
              options={años.map(a => ({ value: a.idAnioEscolar, label: fmtAnioEscolar(a) }))}
            />
            <Select
              label="Nivel"
              value={nivel}
              onChange={handleNivelChange}
              options={niveles.map(n => ({ value: n, label: n }))}
              disabled={!anioId}
            />
            <Select
              label="Grado"
              value={gradoId}
              onChange={handleGradoChange}
              options={gradosFiltrados.map(g => ({ value: g.idGrado, label: g.nombre }))}
              disabled={!nivel}
            />
            <Select
              label="Sección"
              value={seccionId}
              onChange={v => { setSeccionId(v); setError(null); setResultado(null); }}
              options={seccionesFiltradas.map(s => ({ value: s.idSeccion, label: `Sección ${s.nombre}` }))}
              placeholder={gradoId ? (seccionesFiltradas.length === 0 ? 'Sin secciones' : 'Seleccionar…') : 'Primero elige un grado'}
              disabled={!gradoId || seccionesFiltradas.length === 0}
            />
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
                Período
                {loadingPeriodos && <span className="ml-2 font-normal text-slate-400">Cargando…</span>}
              </label>
              <select
                value={periodoId}
                onChange={e => { setPeriodoId(e.target.value); setError(null); setResultado(null); }}
                disabled={!anioId || loadingPeriodos || periodos.length === 0}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm
                           text-slate-700 outline-none focus:ring-2 focus:ring-blue-500
                           disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <option value="">{periodos.length === 0 && !loadingPeriodos ? 'Sin períodos' : 'Seleccionar…'}</option>
                {periodos.map(p => (
                  <option key={p.idPeriodo} value={p.idPeriodo}>{p.nombre}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            onClick={handleGenerar}
            disabled={!canGenerar}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-50
                       disabled:cursor-not-allowed text-white font-semibold text-sm px-5 py-2.5
                       rounded-xl transition-colors shadow-sm"
          >
            {generando ? <><SpinnerIcon /> Generando…</> : (
              <>
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 0 0 3 3.5v13A1.5 1.5 0 0 0 4.5 18h11a1.5 1.5 0 0 0 1.5-1.5V7.621a1.5 1.5 0 0 0-.44-1.06l-4.12-4.122A1.5 1.5 0 0 0 11.378 2H4.5Zm2.25 8.5a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Zm0 3a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Z" clipRule="evenodd"/>
                </svg>
                Boleta por Período
              </>
            )}
          </button>

          <span
            title={!usaCompetencias
              ? 'Active el registro por competencias en Configuración → Evaluación para emitir la boleta SIAGIE.'
              : undefined}
          >
            <button
              onClick={handleGenerarSiagie}
              disabled={!canGenerarSiagie}
              className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50
                         disabled:cursor-not-allowed text-white font-semibold text-sm px-5 py-2.5
                         rounded-xl transition-colors shadow-sm"
            >
              {generandoSiagie ? <><SpinnerIcon /> Generando…</> : (
                <>
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 0 0 3 3.5v13A1.5 1.5 0 0 0 4.5 18h11a1.5 1.5 0 0 0 1.5-1.5V7.621a1.5 1.5 0 0 0-.44-1.06l-4.12-4.122A1.5 1.5 0 0 0 11.378 2H4.5Zm2.25 8.5a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Zm0 3a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Z" clipRule="evenodd"/>
                  </svg>
                  Informe de Progreso SIAGIE
                </>
              )}
            </button>
          </span>

          {!usaCompetencias && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              La boleta SIAGIE requiere activar <b>Habilitar módulo de competencias</b> en Configuración → Evaluación.
            </p>
          )}

          {!canGenerar && !canGenerarSiagie && (
            <p className="text-xs text-slate-400">Selecciona año, nivel, grado y sección para continuar</p>
          )}
          {canGenerarSiagie && !canGenerar && (
            <p className="text-xs text-slate-400">Selecciona también el período para la boleta por período</p>
          )}
        </div>
      </div>

      {/* Resultado */}
      {resultado !== null && (
        <div className="bg-green-50 border border-green-200 rounded-2xl px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
            </svg>
          </div>
          <div>
            <p className="text-green-800 font-semibold text-sm">
              {resultado} boleta{resultado !== 1 ? 's' : ''} generada{resultado !== 1 ? 's' : ''} correctamente
            </p>
            <p className="text-green-600 text-xs mt-0.5">
              Se abrió una ventana con el documento listo para imprimir o guardar como PDF
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center text-red-600 shrink-0">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
            </svg>
          </div>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Instrucciones */}
      <div className="bg-slate-50 rounded-2xl border border-slate-100 p-5">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">¿Cómo funciona?</h3>
        <ol className="space-y-1.5 text-sm text-slate-600">
          <li className="flex gap-2"><span className="text-blue-600 font-bold shrink-0">1.</span> Selecciona el año escolar, grado, sección y período.</li>
          <li className="flex gap-2"><span className="text-blue-600 font-bold shrink-0">2.</span> Haz clic en <strong>"Generar e Imprimir Boletas"</strong>.</li>
          <li className="flex gap-2"><span className="text-blue-600 font-bold shrink-0">3.</span> Se abrirá una ventana con todas las boletas de la sección, una por página.</li>
          <li className="flex gap-2"><span className="text-blue-600 font-bold shrink-0">4.</span> Usa <strong>Imprimir → Guardar como PDF</strong> o envía directamente a la impresora.</li>
        </ol>
      </div>
    </div>
  );
};

export default BoletasMasivaPage;
